import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { hasAuthorization } from "../../../_helpers/hasAuthorization";

type SidebarMenuItem = {
  title: string;
  path: string;
  icon: string;
  module: string;
  activePatterns?: { path: string; regexp: boolean }[];
};

type SidebarGroup = {
  heading: string;
  icon: string;
  items: SidebarMenuItem[];
};

function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /* -----------------------------
     Reinitialize Metronic Menu
  ------------------------------*/
  useEffect(() => {
    const timer = setTimeout(() => {
      const menuElement = document.querySelector("#kt_aside_menu");
      if (menuElement && window.KTMenu) {
        new window.KTMenu(menuElement);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [pathname]);

  /* -----------------------------
     Active Route Check
  ------------------------------*/
  const checkActivePath = (path_string: string, regexp: boolean) => {
    if (!regexp) return pathname === path_string;
    const basePath = path_string.replace("/*", "");
    return pathname.startsWith(basePath);
  };

  const isActive = (item: SidebarMenuItem) => {
    const patterns = item.activePatterns ?? [{ path: item.path, regexp: false }];
    return patterns.some((p) => checkActivePath(p.path, p.regexp));
  };

 
  /* -----------------------------
     MENU GROUPS (ALL ITEMS)
  ------------------------------*/
  const menuGroups: SidebarGroup[] = [
    {
      heading: "Dashboard",
      icon: "ki-home-2",
      items: [
        {
          title: "Dashboards",
          path: "/dashboard",
          icon: "ki-home-2",
		  module: "dashboard",
        },
      ],
    },
    {
      heading: "User Management",
      icon: "ki-security-user",
      items: [
        {
          title: "Users List",
          path: "/users-list",
          icon: "ki-security-user",
		  module: "users",
          activePatterns: [
            { path: "/users-list", regexp: false },
            { path: "/users-list/*", regexp: true },
          ],
        },
		{
          title: "Customers List",
          path: "/customers-list",
          icon: "ki-security-user",
		  module: "customers",
          activePatterns: [
            { path: "/customers-list", regexp: false },
            { path: "/customers-list/*", regexp: true },
          ],
        },
		{
          title: "Pandits List",
          path: "/pandit-list",
          icon: "ki-security-user",
		  module: "pandits",
          activePatterns: [
            { path: "/pandit-list", regexp: false },
            { path: "/pandit-list/*", regexp: true },
          ],
        },
        {
          title: "Delivery Partner List",
          path: "/delivery-partner-list",
          icon: "ki-truck",
		  module: "delivery_partner",
        },
      ],
    },
    {
      heading: "Product Management",
      icon: "ki-basket",
      items: [
        {
          title: "Category List",
          path: "/category-list",
          icon: "ki-category",
		  module: "category",
        },
        {
          title: "Product List",
          path: "/product-list",
          icon: "ki-basket",
		  module: "products",
          activePatterns: [
            { path: "/product-list", regexp: false },
            { path: "/product-detail/*", regexp: true },
          ],
        },
        {
          title: "E-Puja List",
          path: "/epuja-list",
          icon: "ki-basket",
		  module: "epuja",
        },
        {
          title: "Kundli Prediction List",
          path: "/kundli-prediction-list",
          icon: "ki-calendar",
		  module: "kundli_prediction",
        },
		{
          title: "Yoga Service List",
          path: "/yoga-service-list",
          icon: "ki-calendar",
		  module: "yoga",
        },
      ],
    },
    {
      heading: "Orders & Enquiries",
      icon: "ki-element-5",
      items: [
        {
          title: "Products Orders List",
          path: "/orders-list",
          icon: "ki-element-5",
		  module: "product_orders",
        },
		{
          title: "Epooja Orders List",
          path: "/epooja-orders-list",
          icon: "ki-element-5",
		  module: "epuja_orders",
        },
		{
          title: "Kundli Orders List",
          path: "/kundli-and-prediction-orders-list",
          icon: "ki-element-5",
		  module: "kundli_prediction_orders",
        },
        {
          title: "Cancel Orders List",
          path: "/cancel-orders-list",
          icon: "ki-tag-cross",
		  module: "cancel_orders",
        },
        {
          title: "Service Enquiry List",
          path: "/service-enquiry-list",
          icon: "ki-message-text-2",
		  module: "enquiry",
        },
        {
          title: "Complaint List",
          path: "/complaint-list",
          icon: "ki-message-notif",
		  module: "complaint",
        },
      ],
    },
    {
      heading: "Content Management",
      icon: "ki-book",
      items: [
        {
          title: "Blogs",
          path: "/blog-list",
          icon: "ki-menu",
		  module: "blogs",
        },
        {
          title: "Faqs",
          path: "/faq-list",
          icon: "ki-menu",
		  module: "faq",
        },
        {
          title: "Pages List",
          path: "/page-list",
          icon: "ki-document",
		  module: "page_content",
        },
        {
          title: "Contact Us List",
          path: "/contact-us-list",
          icon: "ki-messages",
		  module: "contact_us",
        },
        {
          title: "News Letter Email List",
          path: "/news-letter-email-list",
          icon: "ki-folder",
		  module: "news_letter",
        },
      ],
    },
    {
      heading: "System",
      icon: "ki-gear",
      items: [
        {
          title: "Coupons List",
          path: "/coupon-list",
          icon: "ki-tag",
		  module: "coupons",
        },
        {
          title: "Settings",
          path: "/settings-list",
          icon: "ki-gear",
		  module: "settings",
          activePatterns: [
            { path: "/settings-list", regexp: false },
            { path: "/settings-list/*", regexp: true },
          ],
        },
      ],
    },
  ];
  
  const filteredMenuGroups = menuGroups
  .map(group => ({
    ...group,
    items: group.items.filter(item => {
      if (!item.module) return true;

      return hasAuthorization("view", item.module);
    }),
  }))
  .filter(group => group.items.length > 0);

  /* -----------------------------
     RENDER
  ------------------------------*/
  return (
    <div
      id="kt_aside"
      className="aside card"
      data-kt-drawer="true"
      data-kt-drawer-name="aside"
      data-kt-drawer-activate="{default: true, lg: false}"
      data-kt-drawer-overlay="true"
      data-kt-drawer-width="{default:'200px', '300px': '250px'}"
      data-kt-drawer-direction="start"
      data-kt-drawer-toggle="#kt_aside_toggle"
    >
      <div className="aside-menu flex-column-fluid px-4">
        <div
          className="hover-scroll-overlay-y mh-100 my-5"
          id="kt_aside_menu_wrapper"
          data-kt-scroll="true"
          data-kt-scroll-activate="true"
          data-kt-scroll-height="auto"
          data-kt-scroll-dependencies="{default: '#kt_aside_footer', lg: '#kt_header, #kt_aside_footer'}"
          data-kt-scroll-wrappers="#kt_aside, #kt_aside_menu"
          data-kt-scroll-offset="{default: '5px', lg: '155px'}"
          style={{ height: "385px" }}
        >
          <div
            className="menu menu-column menu-rounded menu-sub-indention fw-semibold fs-6"
            id="kt_aside_menu"
            data-kt-menu="true"
          >
            {filteredMenuGroups.map((group) => {
              const groupActive = group.items.some((item) =>
                isActive(item)
              );

              return (
                <div
                  key={group.heading}
                  className={`menu-item menu-accordion ${
                    groupActive ? "show" : ""
                  }`}
                  data-kt-menu-trigger="click"
                >
                  {/* Group Header */}
                  <span className="menu-link">
                    <span className="menu-icon">
                      <i
                        className={`ki-outline ${group.icon} text-custom-theme fs-2`}
                      ></i>
                    </span>
                    <span className="menu-title">
                      {group.heading}
                    </span>
                    <span className="menu-arrow"></span>
                  </span>

                  {/* Sub Menu */}
                  <div className="menu-sub menu-sub-accordion">
                    {group.items.map((item) => (
                      <div
                        key={item.path}
                        className="menu-item"
                        onClick={() => navigate(item.path)}
                      >
                        <span
                          className={`menu-link ${
                            isActive(item) ? "active" : ""
                          }`}
                        >
                          <span className="menu-icon">
                            <i
                              className={`ki-outline ${item.icon} fs-2`}
                            ></i>
                          </span>
                          <span className="menu-title">
                            {item.title}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
